import { useState, useEffect } from 'react';
import { Copy, Check, CheckCircle, CreditCard, Tag } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { payments, vouchers as vouchersApi, getToken } from '../services/api';

async function fetchPricing() {
  const token = getToken();
  const res = await fetch(`${process.env.REACT_APP_API_URL}/config/pricing`, {
    headers: {
      'Accept':        'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  return res.json();
}

function Card({ children, className = '' }) {
  const theme = useTheme();
  return (
    <div className={`rounded-2xl p-6 ${className}`} style={{ backgroundColor: theme.card }}>
      {children}
    </div>
  );
}

function CopyField({ label, value }) {
  const theme   = useTheme();
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-xl"
      style={{ backgroundColor: theme.background }}>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: theme.textMuted }}>{label}</p>
        <p className="text-sm font-semibold" style={{ color: theme.text }}>{value || '—'}</p>
      </div>
      <button onClick={copy}
        className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors shrink-0"
        style={{
          backgroundColor: copied ? '#dcfce7' : theme.border,
          color: copied ? '#16a34a' : theme.textMuted,
        }}>
        {copied ? <><Check size={11} />Copied</> : <><Copy size={11} />Copy</>}
      </button>
    </div>
  );
}

export default function Upgrade() {
  const theme          = useTheme();
  const { user, refreshUser } = useAuth();

  const [tab,          setTab]          = useState('eft'); // 'eft' | 'voucher'
  const [pricing,      setPricing]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [pending,      setPending]      = useState(null); // active payment record (awaiting or pending)
  const [checking,     setChecking]     = useState(true);  // initial check for an existing payment
  const [error,        setError]        = useState('');

  // Claim step — user must confirm they've actually made the EFT
  const [claiming,     setClaiming]     = useState(false);
  const [claimed,      setClaimed]      = useState(false);
  const [claimError,   setClaimError]   = useState('');

  // Voucher
  const [code,         setCode]         = useState('');
  const [vLoading,     setVLoading]     = useState(false);
  const [vError,       setVError]       = useState('');
  const [vSuccess,     setVSuccess]     = useState(null);

  const isPro = user?.tier === 'pro';
  const proExpires = user?.pro_expires_at
    ? new Date(user.pro_expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  useEffect(() => {
    fetchPricing().then(res => {
      if (res.success) setPricing(res.data);
      setLoading(false);
    });
  }, []);

  // Resume an existing payment if the user already has one in flight
  useEffect(() => {
    payments.current().then(res => {
      if (res.success && res.data) {
        setPending(res.data);
        if (res.data.status === 'pending') setClaimed(true); // already claimed -> show verifying state
      }
      setChecking(false);
    }).catch(() => setChecking(false));
  }, []);

  // Annual only
  const yearlyPrice = pricing?.yearly_price;

  async function handleInitiate() {
    if (!yearlyPrice) return;
    setError('');
    setSubmitting(true);
    const res = await payments.initiate('yearly', user.email);
    setSubmitting(false);
    if (res.success && res.data) {
      setPending(res.data);
      setClaimed(res.data.status === 'pending'); // guard may return an existing pending payment
      setClaimError('');
    } else {
      setError(res.error || 'Failed to initiate payment');
    }
  }

  // Flips the awaiting payment to pending so it enters the admin queue
  async function handleClaim() {
    if (!pending?.id) return;
    setClaimError('');
    setClaiming(true);
    const res = await payments.claim(pending.id);
    setClaiming(false);
    if (res.success) {
      setClaimed(true);
    } else {
      setClaimError(res.error || 'Something went wrong. Please try again.');
    }
  }

  async function handleRedeem() {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setVError('');
    setVSuccess(null);
    setVLoading(true);
    const res = await vouchersApi.redeem(c);
    setVLoading(false);
    if (res.success) {
      setVSuccess(res.data);
      await refreshUser();
    } else {
      setVError(res.error || 'Invalid code');
    }
  }

  // Still checking for an existing payment — avoid flashing the wrong screen
  if (checking && !vSuccess) {
    return (
      <div className="max-w-lg">
        <div className="h-32 rounded-2xl animate-pulse" style={{ backgroundColor: theme.card }} />
      </div>
    );
  }

  // Already pro
  if (isPro && !vSuccess) {
    return (
      <div className="max-w-lg">
        <h1 className="text-xl font-bold mb-6" style={{ color: theme.text }}>Upgrade</h1>
        <Card>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
              <CheckCircle size={22} color="white" />
            </div>
            <div>
              <p className="font-semibold" style={{ color: theme.text }}>Pro is active</p>
              <p className="text-sm" style={{ color: theme.textMuted }}>Expires {proExpires}</p>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: theme.textMuted }}>
            You already have an active Pro subscription. To extend it, redeem another voucher code below.
          </p>
          <VoucherSection
            code={code} setCode={setCode}
            vLoading={vLoading} vError={vError} vSuccess={vSuccess}
            onRedeem={handleRedeem} theme={theme}
          />
        </Card>
      </div>
    );
  }

  // Voucher redeemed success
  if (vSuccess) {
    return (
      <div className="max-w-lg">
        <h1 className="text-xl font-bold mb-6" style={{ color: theme.text }}>Upgrade</h1>
        <Card>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 bg-gradient-brand">
            <CheckCircle size={22} color="white" />
          </div>
          <p className="font-semibold mb-1" style={{ color: theme.text }}>You're on Pro!</p>
          <p className="text-sm mb-2" style={{ color: theme.textMuted }}>
            {vSuccess.pro_months} month{vSuccess.pro_months !== 1 ? 's' : ''} of Pro activated.
          </p>
          <p className="text-xs" style={{ color: theme.textMuted }}>
            Expires {new Date(vSuccess.expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </Card>
      </div>
    );
  }

  // EFT flow — bank details, then user confirms payment was made
  if (pending) {
    return (
      <div className="max-w-lg">
        <h1 className="text-xl font-bold mb-6" style={{ color: theme.text }}>Complete your payment</h1>
        <Card className="mb-4">
          <div className="flex items-center gap-3 mb-5 p-3 rounded-xl"
            style={{ backgroundColor: theme.background }}>
            <CheckCircle size={18} style={{ color: '#00ACC1' }} />
            <p className="text-sm" style={{ color: theme.text }}>
              Use this reference so we can match your payment automatically.
            </p>
          </div>

          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: theme.textMuted }}>
            Payment Reference
          </p>
          <div className="mb-5">
            <CopyField label="Reference" value={pending.reference} />
          </div>

          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: theme.textMuted }}>
            Bank Details
          </p>
          <div className="space-y-2">
            {pending.bank_details?.name              && <CopyField label="Bank"           value={pending.bank_details.name} />}
            {pending.bank_details?.account_name      && <CopyField label="Account Name"   value={pending.bank_details.account_name} />}
            {pending.bank_details?.account_number    && <CopyField label="Account Number" value={pending.bank_details.account_number} />}
            {pending.bank_details?.branch_code       && <CopyField label="Branch Code"    value={pending.bank_details.branch_code} />}
            {pending.bank_details?.account_type      && <CopyField label="Account Type"   value={pending.bank_details.account_type} />}
            <CopyField label="Amount" value={`R${pending.amount}`} />
          </div>
        </Card>

        {!claimed ? (
          <Card>
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text }}>Made the payment?</p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: theme.textMuted }}>
              Once you've completed the EFT using the reference above, tap below to let us know.
              We'll verify your payment and upgrade your account to Pro.
            </p>
            {claimError && (
              <div className="mb-4 px-4 py-3 rounded-xl text-sm border"
                style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
                {claimError}
              </div>
            )}
            <button onClick={handleClaim} disabled={claiming}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
              {claiming ? 'Submitting...' : "I've made the payment"}
            </button>
          </Card>
        ) : (
          <Card>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={20} style={{ color: '#00ACC1' }} />
              <p className="text-sm font-semibold" style={{ color: theme.text }}>Payment submitted for review</p>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
              Thanks! We'll verify your payment and upgrade your account to Pro, usually within a few
              hours on business days. You'll see Pro active next time you open the app.
            </p>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-xl font-bold mb-1" style={{ color: theme.text }}>Upgrade to Pro</h1>
      <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
        Unlock SARS logbook exports, unlimited vehicles, and more.
      </p>

      {/* Tab toggle */}
      <div className="flex rounded-xl p-1 mb-6" style={{ backgroundColor: theme.card }}>
        {[
          { id: 'eft',     label: 'Buy via EFT',       icon: CreditCard },
          { id: 'voucher', label: 'Redeem Voucher',     icon: Tag },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: tab === id ? '#00ACC1' : 'transparent',
              color: tab === id ? '#fff' : theme.textSecondary,
            }}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'eft' ? (
        <>
          {/* Annual plan */}
          {loading ? (
            <div className="h-24 rounded-2xl animate-pulse mb-4" style={{ backgroundColor: theme.card }} />
          ) : (
            <div className="rounded-2xl p-5 mb-4 border-2" style={{ backgroundColor: theme.card, borderColor: '#00ACC1' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm mb-1" style={{ color: theme.text }}>Annual Plan</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>12 months of Pro</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold" style={{ color: '#00ACC1' }}>R{yearlyPrice}</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>per year</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl text-sm border"
              style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
              {error}
            </div>
          )}

          <button onClick={handleInitiate} disabled={submitting || loading}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
            {submitting ? 'Processing...' : `Continue to Payment — R${yearlyPrice ?? '...'}`}
          </button>
          <p className="text-xs text-center mt-3" style={{ color: theme.textMuted }}>
            You'll receive bank details to complete an EFT transfer.
          </p>
        </>
      ) : (
        <Card>
          <VoucherSection
            code={code} setCode={setCode}
            vLoading={vLoading} vError={vError} vSuccess={vSuccess}
            onRedeem={handleRedeem} theme={theme}
          />
        </Card>
      )}

      {/* Pro features list */}
      <div className="mt-6 rounded-2xl p-5" style={{ backgroundColor: theme.card }}>
        <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: theme.textMuted }}>
          What's included
        </p>
        <div className="space-y-2.5">
          {[
            'SARS-compliant logbook export (PDF & CSV)',
            'Unlimited vehicles',
            'Unlimited trips per month',
            'Full trip history',
            'Custom trip reasons',
          ].map(f => (
            <div key={f} className="flex items-center gap-2.5">
              <CheckCircle size={15} style={{ color: '#00ACC1', shrink: 0 }} />
              <span className="text-sm" style={{ color: theme.textSecondary }}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Shared voucher redeem section
function VoucherSection({ code, setCode, vLoading, vError, vSuccess, onRedeem, theme }) {
  return (
    <div>
      <p className="text-sm font-semibold mb-1" style={{ color: theme.text }}>Redeem a voucher code</p>
      <p className="text-sm mb-4" style={{ color: theme.textMuted }}>
        Received a code from your dealer or via email? Enter it below.
      </p>
      {vError && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm border"
          style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
          {vError}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && onRedeem()}
          placeholder="e.g. TDP-A3F8B2C9"
          className="flex-1 px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors"
          style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
        />
        <button onClick={onRedeem} disabled={vLoading || !code.trim()}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
          {vLoading ? '...' : 'Redeem'}
        </button>
      </div>
    </div>
  );
}